import { AudioItem } from '../../items/audio/audio-item-type';
import {preserveItemAnimationWindow} from '@waka/compat/animation-window';
import { EditorStarterItem } from '../../items/item-type';
import { VideoItem } from '../../items/video/video-item-type';
import {
  AudioFadableItem,
  getCanFadeAudio,
  getCanFadeVisual,
  VisuallyFadableItem,
} from '../../utils/fade';
import { getNonSilenceSegments } from '../../utils/silence-detection';
import type { SilenceSegment } from '../../utils/silence-detection';
import { generateRandomId } from '../../utils/generate-random-id';
import { deleteItems } from './delete-items';
import { EditorState, TrackType } from '../types';

export interface RemoveSilenceParams {
  state: EditorState;
  itemId: string;
  silenceSegments: SilenceSegment[];
}

/**
 * Replaces a single video/audio item with multiple items by removing silence segments.
 * Non-silence segments become separate items; items after the clip shift left.
 */
export function removeSilence({
  state,
  itemId,
  silenceSegments,
}: RemoveSilenceParams): EditorState {
  const targetItem = state.undoableState.items[itemId] as
    | EditorStarterItem
    | undefined;

  if (!targetItem) return state;

  const isVideo = targetItem.type === 'video';
  const isAudio = targetItem.type === 'audio';
  if (!isVideo && !isAudio) return state;

  let targetTrack: TrackType | undefined;
  let targetTrackIndex = -1;

  for (let i = 0; i < state.undoableState.tracks.length; i++) {
    const track = state.undoableState.tracks[i];
    if (!track?.items.includes(itemId)) continue;
    targetTrack = track;
    targetTrackIndex = i;
    break;
  }

  if (!targetTrack || targetTrackIndex === -1) return state;

  const fps = state.undoableState.fps;
  const itemFrom = targetItem.from;
  const itemDurationInFrames = targetItem.durationInFrames;
  const itemDurationInSeconds = itemDurationInFrames / fps;

  const mediaStartInSeconds = isVideo
    ? (targetItem as VideoItem).videoStartFromInSeconds
    : (targetItem as AudioItem).audioStartFromInSeconds;

  const nonSilence = getNonSilenceSegments(
    itemDurationInSeconds,
    silenceSegments,
  );

  // 전체가 무음이면 아이템 삭제 (트랙 링크 켜져 있으면 연결된 비디오/오디오 함께 삭제)
  if (nonSilence.length === 0) {
    return deleteItems(state, [itemId], {
      isLinkingEnabled: state.isLinkingEnabled,
    });
  }

  const newItemsList: EditorStarterItem[] = [];
  let timelineFrom = itemFrom;

  for (let i = 0; i < nonSilence.length; i++) {
    const seg = nonSilence[i]!;
    const segDurationInSeconds = seg.endInSeconds - seg.startInSeconds;
    const segDurationInFrames = Math.round(segDurationInSeconds * fps);
    if (segDurationInFrames <= 0) continue;

    const newItem: EditorStarterItem = {
      ...preserveItemAnimationWindow(targetItem,fps,Math.round(seg.startInSeconds*fps)),
      id: generateRandomId(),
      from: timelineFrom,
      durationInFrames: segDurationInFrames,
    };

    if (targetItem.type === 'video') {
      (newItem as VideoItem).videoStartFromInSeconds =
        mediaStartInSeconds + seg.startInSeconds;
    }
    if (targetItem.type === 'audio') {
      (newItem as AudioItem).audioStartFromInSeconds =
        mediaStartInSeconds + seg.startInSeconds;
    }
    const segDurationSec = segDurationInSeconds;
    const isFirst = i === 0;
    const isLast = i === nonSilence.length - 1;

    if (getCanFadeVisual(targetItem) && !newItem.visualFadeWindow) {
      (newItem as VisuallyFadableItem).fadeInDurationInSeconds = isFirst
        ? Math.min(
            (targetItem as VisuallyFadableItem).fadeInDurationInSeconds ?? 0,
            segDurationSec,
          )
        : 0;
      (newItem as VisuallyFadableItem).fadeOutDurationInSeconds = isLast
        ? Math.min(
            (targetItem as VisuallyFadableItem).fadeOutDurationInSeconds ?? 0,
            segDurationSec,
          )
        : 0;
    }

    if (getCanFadeAudio(targetItem) && !newItem.audioFadeWindow) {
      (newItem as AudioFadableItem).audioFadeInDurationInSeconds = isFirst
        ? Math.min(
            (targetItem as AudioFadableItem).audioFadeInDurationInSeconds ?? 0,
            segDurationSec,
          )
        : 0;
      (newItem as AudioFadableItem).audioFadeOutDurationInSeconds = isLast
        ? Math.min(
            (targetItem as AudioFadableItem).audioFadeOutDurationInSeconds ?? 0,
            segDurationSec,
          )
        : 0;
    }

    newItemsList.push(newItem);
    timelineFrom += segDurationInFrames;
  }

  const totalNewDurationInFrames = timelineFrom - itemFrom;
  const removedFrames = itemDurationInFrames - totalNewDurationInFrames;
  if (removedFrames <= 0) return state;

  const itemEnd = itemFrom + itemDurationInFrames;

  const newItemsRecord: Record<string, EditorStarterItem> = {
    ...state.undoableState.items,
  };
  delete newItemsRecord[itemId];
  for (const it of newItemsList) {
    newItemsRecord[it.id] = it;
  }

  for (const id of Object.keys(newItemsRecord)) {
    const it = newItemsRecord[id]!;
    if (it.from >= itemEnd) {
      newItemsRecord[id] = { ...it, from: it.from - removedFrames };
    }
  }

  const newTracks = state.undoableState.tracks.map(
    (track, index): TrackType => {
      if (index !== targetTrackIndex) {
        return track;
      }
      // shift 이후의 newItemsRecord 기준으로 필터링해야 올바른 위치 판단 가능
      const before = track.items.filter(
        (id) => id !== itemId && (newItemsRecord[id]?.from ?? 0) < itemFrom,
      );
      const after = track.items.filter(
        (id) =>
          id !== itemId &&
          newItemsRecord[id] !== undefined &&
          (newItemsRecord[id]?.from ?? 0) >= itemFrom,
      );
      return {
        ...track,
        items: [...before, ...newItemsList.map((it) => it.id), ...after],
      };
    },
  );

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      tracks: newTracks,
      items: newItemsRecord,
    },
    selectedItems: newItemsList.map((it) => it.id),
  };
}
